import React, { useState, useEffect } from 'react';
import { authFetch } from '../services/authFetch';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Alert,
  TextField,
  Link,
  TablePagination,
  Tooltip,
  Chip,
  Button,
} from '@mui/material';
import { LockOutlined } from '@mui/icons-material';
import { useGlobalFilters } from '../contexts/FilterContext';
import { fetchMVCPortfolios, fetchMVCPlatforms, fetchMVCApplications } from '../services/mvcViewService';
import { useMsal } from '@azure/msal-react';

interface TopsOnboardingData {
  portfolioName: string;
  platformName: string;
  appName: string;
  appId: string;
  serviceOwner: string;
  serviceOwnerEmail: string;
  cmdbCompleteness: string;
  l15ApplicationSupport: string;
  statusPage: string;
  mvc: string;
  mop: string;
  hedwig: string;
  p1p2Incidents: number;
  p3p4Incidents: number;
  changeManagement: string;
  postmortems: string;
}

interface ApplicationCertificationItem {
  name: string;
  u_application_id: string;
  'u_business_platform.value': string;
  owned_by_name: string;
  managed_by_name: string | null;
  u_engineering_manager_name: string | null;
  u_sr_engineering_manager_name: string | null;
  u_engineering_director_name: string | null;
  portfolio: string;
  platform: string;
  portfolio_id: string;
  platform_id: string;
  has_server_relation: boolean;
  has_app_relation: boolean;
  has_cloud: boolean;
  is_saas: boolean;
  mac_owners: string;
  status: string;
  is_mvc: boolean;
  is_bca: boolean;
  has_repo: boolean;
  is_standalone: boolean;
  test_app: boolean;
  verified: string | null;
  verified_at: string | null;
  app_verified: string | null;
  app_verified_at: string | null;
  own_verified: string;
  own_verified_at: string;
  verified_owner: string | null;
  app_verified_owner: string | null;
  own_verified_owner: string;
  verified_expired: string | null;
  app_verified_expired: string | null;
  completeness_percentage: number;
  certification_percentage: number;
  id: number;
  overall_app_id_completeness_percentage: number;
  overall_app_id_certification_percentage: number;
}

interface ApplicationCertificationResponse {
  combined_data: ApplicationCertificationItem[];
}

interface ChangeOnboardingItem {
  application_id: string;
  application_name: string;
  platform: string;
  portfolio: string;
  app_owners: string | null;
  bca_status: boolean | null;
  mvc_status: boolean | null;
  onboarding_info: {
    onboarding_status: string;
    deployment_channel: string;
    change_process_type: string;
    last_change_date: string;
  } | null;
  owners_role_mac?: string;
  change_success_rate?: number | null;
}

interface ChangeOnboardingResponse {
  status: number;
  data: ChangeOnboardingItem[];
}

const normalizeChangeOnboardingByAppId = (items: ChangeOnboardingItem[]): ChangeOnboardingItem[] => {
  const onboardedAppIds = new Set<string>();

  items.forEach((item) => {
    const appId = item.application_id?.trim();
    const status = item.onboarding_info?.onboarding_status?.toLowerCase();

    if (appId && status === 'onboarded') {
      onboardedAppIds.add(appId);
    }
  });

  if (onboardedAppIds.size === 0) {
    return items;
  }

  return items.map((item) => {
    const appId = item.application_id?.trim();
    if (!appId || !onboardedAppIds.has(appId)) {
      return item;
    }

    return {
      ...item,
      onboarding_info: {
        onboarding_status: 'onboarded',
        deployment_channel: item.onboarding_info?.deployment_channel || 'N/A',
        change_process_type: item.onboarding_info?.change_process_type || 'N/A',
        last_change_date: item.onboarding_info?.last_change_date || '',
      },
    };
  });
};

const TOPS_ONBOARDING_ALLOWED_EMAILS = new Set([
  'rahul.naidu@maersk.com',
  'touseef.mohammed@maersk.com',
  'durand.rodrigues@maersk.com',
  'nitish.yn@maersk.com',
  'k.harish@maersk.com',
]);

const TopsOnboarding: React.FC = () => {
  const { selectedPortfolio, setSelectedPortfolio, selectedPlatform, setSelectedPlatform } = useGlobalFilters();
  const { accounts } = useMsal();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [portfolioOptions, setPortfolioOptions] = useState<string[]>([]);
  const [platformOptions, setPlatformOptions] = useState<string[]>([]);
  const [onboardingData, setOnboardingData] = useState<TopsOnboardingData[]>([]);
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>('30d');
  const [customDateRange, setCustomDateRange] = useState<{ start: string; end: string }>({ start: '', end: '' });
  const [customDateApplied, setCustomDateApplied] = useState<boolean>(false);
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [certificationData, setCertificationData] = useState<ApplicationCertificationItem[]>([]);
  const [changeOnboardingData, setChangeOnboardingData] = useState<ChangeOnboardingItem[]>([]);
  const [reliabilityFrameworkData, setReliabilityFrameworkData] = useState<any[]>([]);
  const [incidentsData, setIncidentsData] = useState<any[]>([]); // P1/P2 incidents data
  const [p3p4IncidentsData, setP3P4IncidentsData] = useState<any[]>([]); // P3/P4 incidents data
  const [postmortemDetailsData, setPostmortemDetailsData] = useState<any[]>([]); // Postmortem data

  // Helper function to get user details from active account
  const getUserDetails = () => {
    if (accounts && accounts.length > 0) {
      return {
        name: accounts[0].name || accounts[0].username || 'User',
        email: accounts[0].username || ''
      };
    }
    return null;
  };

  // Define all functions first
  const loadPortfolios = async () => {
    try {
      console.log('🔄 Loading portfolio options...');
      const portfolios = await fetchMVCPortfolios();
      setPortfolioOptions(portfolios);
      console.log('✅ Loaded', portfolios.length, 'portfolios');
    } catch (err) {
      console.error('❌ Error loading portfolios:', err);
      setError('Failed to load portfolio options');
    }
  };

  const loadPlatforms = async () => {
    try {
      console.log('🔄 Loading platform options for portfolio:', selectedPortfolio);
      
      // If "all" is selected, load all platforms
      // Otherwise, load platforms filtered by selected portfolio
      const portfolio = selectedPortfolio === 'all' ? undefined : selectedPortfolio;
      const platforms = await fetchMVCPlatforms(portfolio);
      setPlatformOptions(platforms);
      console.log('✅ Loaded', platforms.length, 'platforms');
      
      // Reset platform selection if current platform is not in the filtered list
      if (selectedPlatform !== 'all' && !platforms.includes(selectedPlatform)) {
        console.log('⚠️ Resetting platform filter as current selection not in filtered list');
        setSelectedPlatform('all');
      }
    } catch (err) {
      console.error('❌ Error loading platforms:', err);
      setError('Failed to load platform options');
    }
  };

  // Fetch Application Certification data from API
  const loadCertificationData = async () => {
    const userDetails = getUserDetails();
    if (!userDetails || !userDetails.email) {
      console.warn('⚠️ User details not available yet');
      return;
    }

    try {
      console.log('🔄 Loading Application Certification data...');
      
      console.log('👤 User email:', userDetails.email);
      
      const certificationBaseUrl = process.env.REACT_APP_APPLICATION_CERTIFICATION_DASHBOARD_API;
      console.log('🌐 Certification API URL:', certificationBaseUrl);
      
      const apiUrl = `${certificationBaseUrl}/get-certification-dashboard-data`;
      console.log('📡 Fetching from:', apiUrl);
      
      const response = await authFetch(
        apiUrl,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      console.log('📥 Response status:', response.status, response.statusText);

      if (!response.ok) {
        throw new Error(`Failed to fetch certification data: ${response.status} ${response.statusText}`);
      }
      
      const result: ApplicationCertificationResponse = await response.json();
      setCertificationData(result.combined_data || []);
      console.log('✅ Loaded', (result.combined_data || []).length, 'certification records');
      
      // Log first few app IDs for debugging
      if (result.combined_data && result.combined_data.length > 0) {
        console.log('📋 Sample App IDs from Certification:', result.combined_data.slice(0, 5).map(item => item.u_application_id));
      }
    } catch (err) {
      console.error('❌ Error fetching certification data:', err);
      // Don't set error state, just log it - this is supplementary data
    }
  };

  // Helper function to calculate date range based on selected time range
  const getDateRange = () => {
    const endDate = new Date();
    let startDate = new Date();
    
    if (selectedTimeRange === 'custom' && customDateApplied && customDateRange.start && customDateRange.end) {
      // Use custom date range
      startDate = new Date(customDateRange.start);
      return {
        start: customDateRange.start,
        end: customDateRange.end
      };
    } else if (selectedTimeRange === '7d') {
      // Last 7 days
      startDate.setDate(endDate.getDate() - 7);
    } else if (selectedTimeRange === '30d') {
      // Last 30 days (default)
      startDate.setDate(endDate.getDate() - 30);
    } else if (selectedTimeRange === '90d') {
      // Last 90 days
      startDate.setDate(endDate.getDate() - 90);
    } else if (selectedTimeRange === 'ytd') {
      // Year to Date
      startDate = new Date(endDate.getFullYear(), 0, 1);
    }
    
    return {
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    };
  };

  // Fetch Reliability Framework data from API
  const loadReliabilityFrameworkData = async () => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
      console.log('🔄 Fetching Reliability Framework data...');
      
      const response = await authFetch(
        `${apiBaseUrl}/api/reliability-framework/portfolio-overview`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch reliability framework data: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      setReliabilityFrameworkData(result.data || []);
      console.log('✅ Loaded', (result.data || []).length, 'reliability framework records');
      
      // Log first few records for debugging
      if (result.data && result.data.length > 0) {
        console.log('📋 Sample Reliability Framework data:', result.data.slice(0, 3).map((item: any) => ({
          app_id: item.app_id,
          monitoring: item.monitoring,
          hedwig_rota: item.hedwig_rota
        })));
      }
    } catch (err) {
      console.error('❌ Error fetching reliability framework data:', err);
      // Don't set error state, just log it - this is supplementary data
    }
  };

  // Load P1/P2 Incidents data filtered by date range
  const loadIncidentsData = async () => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
      console.log('🔄 Fetching P1/P2 Incidents data...');
      
      // Build request payload with date filters
      const dateRange = getDateRange();
      const payload: any = {
        start_time: dateRange.start,
        end_time: dateRange.end
      };
      
      console.log('📅 Date filter applied:', payload);
      
      const response = await authFetch(
        `${apiBaseUrl}/api/p1-p2-incidents`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch incidents data: ${response.status}`);
      }
      
      const result = await response.json();
      const incidents = result.data || [];
      setIncidentsData(incidents);
      console.log('✅ Loaded', incidents.length, 'P1/P2 incidents');
      
      // Log sample data to verify the mapping
      if (incidents.length > 0) {
        console.log('📋 Sample Incidents data:', incidents.slice(0, 3).map((item: any) => ({
          number: item.number,
          priority: item.priority,
          cmdb_ci_u_application_id: item.cmdb_ci_u_application_id,
          short_description: item.short_description
        })));
      }
    } catch (err) {
      console.error('❌ Error fetching incidents data:', err);
      // Don't set error state, just log it - this is supplementary data
    }
  };

  // Load P3/P4 Incidents data filtered by date range
  const loadP3P4IncidentsData = async () => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
      console.log('🔄 Fetching P3/P4 Incidents data...');
      
      // Build request payload with date filters
      const dateRange = getDateRange();
      const payload: any = {
        start_time: dateRange.start,
        end_time: dateRange.end
      };
      
      console.log('📅 Date filter applied:', payload);
      
      const response = await authFetch(
        `${apiBaseUrl}/api/p3-p4-incidents`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch P3/P4 incidents data: ${response.status}`);
      }
      
      const result = await response.json();
      const incidents = result.data || [];
      setP3P4IncidentsData(incidents);
      console.log('✅ Loaded', incidents.length, 'P3/P4 incidents');
      
      // Log sample data to verify the mapping
      if (incidents.length > 0) {
        console.log('📋 Sample P3/P4 Incidents data:', incidents.slice(0, 3).map((item: any) => ({
          number: item.number,
          priority: item.priority,
          cmdb_ci_u_application_id: item.cmdb_ci_u_application_id,
          short_description: item.short_description
        })));
      }
    } catch (err) {
      console.error('❌ Error fetching P3/P4 incidents data:', err);
      // Don't set error state, just log it - this is supplementary data
    }
  };

  // Load Postmortem data
  const loadPostmortemData = async () => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
      console.log('🔄 Fetching Postmortem data...');
      
      const response = await authFetch(
        `${apiBaseUrl}/api/postmortem/all-reports`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        console.warn('⚠️ Postmortem API error! status:', response.status);
        return;
      }
      
      const responseData = await response.json();
      
      // Handle both wrapped and unwrapped responses
      let allPostmortems;
      if (Array.isArray(responseData)) {
        allPostmortems = responseData;
      } else if (responseData && typeof responseData === 'object') {
        allPostmortems = responseData.data || responseData.items || responseData.postmortems || responseData.results || [];
        if (!Array.isArray(allPostmortems)) {
          console.error('❌ Unexpected response structure:', responseData);
          allPostmortems = [];
        }
      } else {
        console.error('❌ Invalid response format:', responseData);
        allPostmortems = [];
      }
      
      console.log('✅ Loaded', allPostmortems.length, 'postmortems');
      
      // Filter for P1/P2 only
      const filteredPostmortems = allPostmortems.filter((pm: any) => 
        pm.priority === "1" || pm.priority === "2"
      );
      
      setPostmortemDetailsData(filteredPostmortems);
      console.log('✅ Filtered to', filteredPostmortems.length, 'P1/P2 postmortems');
      
      // Log sample data
      if (filteredPostmortems.length > 0) {
        console.log('📋 Sample Postmortem data:', filteredPostmortems.slice(0, 3).map((item: any) => ({
          incidentNumber: item.incidentNumber,
          causedBy_app: item.causedBy_app,
          priority: item.priority
        })));
      }
    } catch (err) {
      console.error('❌ Error fetching postmortem data:', err);
      // Don't set error state, just log it - this is supplementary data
    }
  };

  // Fetch Change Onboarding data from API
  const loadChangeOnboardingData = async () => {
    const userDetails = getUserDetails();
    if (!userDetails || !userDetails.email) {
      console.warn('⚠️ User details not available yet for Change Onboarding');
      return;
    }

    try {
      console.log('🔄 Loading Change Onboarding data...');
      
      console.log('👤 User email:', userDetails.email);
      
      const onboardingBaseUrl = process.env.REACT_APP_CHANGE_ONBOARDING_API;
      console.log('🌐 Change Onboarding API URL:', onboardingBaseUrl);
      
      const apiUrl = `${onboardingBaseUrl}/change/get_all_applications`;
      console.log('📡 Fetching from:', apiUrl);
      
      const response = await authFetch(
        apiUrl,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      console.log('📥 Change Onboarding Response status:', response.status, response.statusText);

      if (!response.ok) {
        throw new Error(`Failed to fetch change onboarding data: ${response.status} ${response.statusText}`);
      }
      
      const result: ChangeOnboardingResponse = await response.json();
      const normalizedOnboardingData = normalizeChangeOnboardingByAppId(result.data || []);
      setChangeOnboardingData(normalizedOnboardingData);
      console.log('✅ Loaded', (result.data || []).length, 'change onboarding records');
      
      // Log first few app IDs for debugging
      if (result.data && result.data.length > 0) {
        console.log('📋 Sample App IDs from Change Onboarding:', result.data.slice(0, 5).map(item => item.application_id));
      }
    } catch (err) {
      console.error('❌ Error fetching change onboarding data:', err);
      // Don't set error state, just log it - this is supplementary data
    }
  };

  const loadOnboardingData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔄 Loading onboarding data...');
      console.log('📊 Certification Data available:', certificationData.length, 'records');
      console.log('📊 Change Onboarding Data available:', changeOnboardingData.length, 'records');
      console.log('📊 Reliability Framework Data available:', reliabilityFrameworkData.length, 'records');
      
      // Fetch MAC applications with filters
      const portfolio = selectedPortfolio === 'all' ? undefined : selectedPortfolio;
      const platform = selectedPlatform === 'all' ? undefined : selectedPlatform;
      
      const applications = await fetchMVCApplications(portfolio, platform, undefined, true);
      console.log('📦 Fetched', applications.length, 'MAC applications');
      
      // Log first few app IDs for debugging
      if (applications.length > 0) {
        console.log('📋 Sample App IDs from MAC:', applications.slice(0, 5).map(app => app.app_id));
      }
      
      // Create a map of Certification data by u_application_id for quick lookup
      const certificationMap = new Map<string, ApplicationCertificationItem>();
      certificationData.forEach(item => {
        if (item.u_application_id) {
          certificationMap.set(item.u_application_id, item);
        }
      });
      console.log('🗺️ Created Certification map with', certificationMap.size, 'entries');
      
      // Create a map of Change Onboarding data by application_id for quick lookup
      const changeOnboardingMap = new Map<string, ChangeOnboardingItem>();
      changeOnboardingData.forEach(item => {
        if (item.application_id) {
          changeOnboardingMap.set(item.application_id, item);
        }
      });
      console.log('🗺️ Created Change Onboarding map with', changeOnboardingMap.size, 'entries');
      
      // Create a map of Reliability Framework data by app_id for quick lookup
      const reliabilityFrameworkMap = new Map<string, any>();
      reliabilityFrameworkData.forEach(item => {
        if (item.app_id) {
          reliabilityFrameworkMap.set(item.app_id, item);
        }
      });
      console.log('🗺️ Created Reliability Framework map with', reliabilityFrameworkMap.size, 'entries');
      
      // Create a map of P1/P2 Incidents count by app_id for quick lookup
      const p1p2IncidentsCountMap = new Map<string, number>();
      incidentsData.forEach(incident => {
        const appId = incident.cmdb_ci_u_application_id;
        if (appId) {
          const currentCount = p1p2IncidentsCountMap.get(appId) || 0;
          p1p2IncidentsCountMap.set(appId, currentCount + 1);
        }
      });
      console.log('🗺️ Created P1/P2 Incidents count map with', p1p2IncidentsCountMap.size, 'unique app IDs');
      console.log('📊 Total P1/P2 incidents across all apps:', incidentsData.length);
      
      // Create a map of P3/P4 Incidents count by app_id for quick lookup
      const p3p4IncidentsCountMap = new Map<string, number>();
      p3p4IncidentsData.forEach(incident => {
        const appId = incident.cmdb_ci_u_application_id;
        if (appId) {
          const currentCount = p3p4IncidentsCountMap.get(appId) || 0;
          p3p4IncidentsCountMap.set(appId, currentCount + 1);
        }
      });
      console.log('🗺️ Created P3/P4 Incidents count map with', p3p4IncidentsCountMap.size, 'unique app IDs');
      console.log('📊 Total P3/P4 incidents across all apps:', p3p4IncidentsData.length);
      
      // Create a map of Postmortem data by caused_by_app for quick lookup
      const postmortemMap = new Map<string, any[]>();
      postmortemDetailsData.forEach(pm => {
        const appId = pm.causedBy_app;
        if (appId) {
          if (!postmortemMap.has(appId)) {
            postmortemMap.set(appId, []);
          }
          postmortemMap.get(appId)?.push(pm);
        }
      });
      console.log('🗺️ Created Postmortem map with', postmortemMap.size, 'unique app IDs');
      console.log('📊 Total P1/P2 postmortems:', postmortemDetailsData.length);
      
      // Transform MAC data to TopsOnboarding format
      let certificationMatchedCount = 0;
      let changeOnboardingMatchedCount = 0;
      let reliabilityFrameworkMatchedCount = 0;
      let p1p2IncidentsMatchedCount = 0;
      let p3p4IncidentsMatchedCount = 0;
      let postmortemMatchedCount = 0;
      const transformedData: TopsOnboardingData[] = applications.map(app => {
        const appId = app.app_id || 'N/A';
        
        // Look up Certification data for this app
        const certificationInfo = certificationMap.get(appId);
        
        // Look up Change Onboarding data for this app
        const changeOnboardingInfo = changeOnboardingMap.get(appId);
        
        // Look up Reliability Framework data for this app
        const reliabilityFrameworkInfo = reliabilityFrameworkMap.get(appId);
        
        // Look up P1/P2 Incidents count for this app
        const p1p2IncidentsCount = p1p2IncidentsCountMap.get(appId) || 0;
        
        // Look up P3/P4 Incidents count for this app
        const p3p4IncidentsCount = p3p4IncidentsCountMap.get(appId) || 0;
        
        // Look up Postmortem data for this app
        const postmortems = postmortemMap.get(appId) || [];
        
        if (certificationInfo) {
          certificationMatchedCount++;
          console.log('✅ Certification match for App ID:', appId, '- Completeness:', certificationInfo.overall_app_id_completeness_percentage + '%');
        }
        
        if (changeOnboardingInfo) {
          changeOnboardingMatchedCount++;
          console.log('✅ Change Onboarding match for App ID:', appId, '- Status:', changeOnboardingInfo.onboarding_info?.onboarding_status);
        }
        
        if (reliabilityFrameworkInfo) {
          reliabilityFrameworkMatchedCount++;
        }
        
        if (p1p2IncidentsCount > 0) {
          p1p2IncidentsMatchedCount++;
        }
        
        if (p3p4IncidentsCount > 0) {
          p3p4IncidentsMatchedCount++;
        }
        
        if (postmortems.length > 0) {
          postmortemMatchedCount++;
        }
        
        // Determine CMDB Completeness based on Application Certification
        let cmdbCompleteness = 'N/A';
        if (certificationInfo && certificationInfo.overall_app_id_completeness_percentage !== null && certificationInfo.overall_app_id_completeness_percentage !== undefined) {
          // Display as percentage
          cmdbCompleteness = `${Math.round(certificationInfo.overall_app_id_completeness_percentage)}%`;
        }
        
        // Determine L1.5 Support Requirements from certification data
        // Extract from Support Group fields (support_group, support_group_l2, support_group_l3, support_group_l4)
        let l15SupportStatus = 'N/A';
        if (certificationInfo) {
          // Check if any support group field is populated
          const hasSupportGroup = certificationInfo.owned_by_name || 
                                  certificationInfo.managed_by_name || 
                                  certificationInfo.u_engineering_manager_name || 
                                  certificationInfo.u_sr_engineering_manager_name;
          
          if (hasSupportGroup) {
            l15SupportStatus = 'Yes';
          } else {
            l15SupportStatus = 'No';
          }
        }
        
        // Determine Change Management status based on Change Onboarding status
        let changeManagementStatus = 'Not Onboarded';
        if (changeOnboardingInfo && changeOnboardingInfo.onboarding_info) {
          const status = changeOnboardingInfo.onboarding_info.onboarding_status;
          // Capitalize first letter for display
          if (status === 'onboarded') {
            changeManagementStatus = 'Onboarded';
          } else if (status === 'not onboarded') {
            changeManagementStatus = 'Not Onboarded';
          } else {
            changeManagementStatus = status.charAt(0).toUpperCase() + status.slice(1);
          }
        }
        
        // Get MOP (Monitoring) data from Reliability Framework
        let mopStatus = 'N/A';
        if (reliabilityFrameworkInfo && reliabilityFrameworkInfo.monitoring) {
          // Normalize the value to Yes/No/NA
          const monitoring = String(reliabilityFrameworkInfo.monitoring).trim();
          if (monitoring.toUpperCase() === 'YES') {
            mopStatus = 'Yes';
          } else if (monitoring.toUpperCase() === 'NO') {
            mopStatus = 'No';
          } else if (monitoring.toUpperCase() === 'NA' || monitoring.toUpperCase() === 'N/A') {
            mopStatus = 'N/A';
          } else {
            mopStatus = monitoring;
          }
        }
        
        // Get Hedwig (Hedwig Go Alert Rota) data from Reliability Framework
        let hedwigStatus = 'N/A';
        if (reliabilityFrameworkInfo && reliabilityFrameworkInfo.hedwig_rota) {
          // Normalize the value to Yes/No/NA
          const hedwig = String(reliabilityFrameworkInfo.hedwig_rota).trim();
          if (hedwig.toUpperCase() === 'YES') {
            hedwigStatus = 'Yes';
          } else if (hedwig.toUpperCase() === 'NO') {
            hedwigStatus = 'No';
          } else if (hedwig.toUpperCase() === 'NA' || hedwig.toUpperCase() === 'N/A') {
            hedwigStatus = 'N/A';
          } else {
            hedwigStatus = hedwig;
          }
        }
        
        // Determine Postmortem status based on P1/P2 incidents
        // Logic: If P1/P2 exists → Yes, If no P1/P2 → Not Available
        let postmortemStatus = 'Not Available';
        if (p1p2IncidentsCount > 0) {
          postmortemStatus = 'Yes';
        }
        
        return {
          portfolioName: app.portfolio_name || 'N/A',
          platformName: app.platform_name || 'N/A',
          appName: app.app_name || 'N/A',
          appId: appId,
          serviceOwner: app.owner_names || 'N/A',
          serviceOwnerEmail: app.owners || 'N/A',
          cmdbCompleteness: cmdbCompleteness,
          l15ApplicationSupport: l15SupportStatus,
          statusPage: 'Yes', // All applications have Status Page
          mvc: 'Yes', // All applications have MVC status as Yes
          mop: mopStatus, // From Reliability Framework monitoring column
          hedwig: hedwigStatus, // From Reliability Framework hedwig_rota column
          p1p2Incidents: p1p2IncidentsCount,
          p3p4Incidents: p3p4IncidentsCount,
          changeManagement: changeManagementStatus, // From Change Onboarding
          postmortems: postmortemStatus, // Based on P1/P2 incidents existence
        };
      });
      
      setOnboardingData(transformedData);
      console.log('✅ Loaded', transformedData.length, 'applications');
      console.log('🔗 Matched', certificationMatchedCount, 'applications with Certification data');
      console.log('🔗 Matched', changeOnboardingMatchedCount, 'applications with Change Onboarding data');
      console.log('🔗 Matched', reliabilityFrameworkMatchedCount, 'applications with Reliability Framework data');
      console.log('🔗 Matched', p1p2IncidentsMatchedCount, 'applications with P1/P2 incidents');
      console.log('🔗 Matched', p3p4IncidentsMatchedCount, 'applications with P3/P4 incidents');
      console.log('🔗 Matched', postmortemMatchedCount, 'applications with Postmortems');
      setPage(0); // Reset to first page when data changes
    } catch (err) {
      console.error('❌ Error loading onboarding data:', err);
      setError('Failed to load onboarding data');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // useEffects - call functions defined above
  
  // Load Certification data when user is authenticated
  useEffect(() => {
    if (accounts && accounts.length > 0) {
      loadCertificationData();
    }
  }, [accounts]);

  // Load Change Onboarding data when user is authenticated
  useEffect(() => {
    if (accounts && accounts.length > 0) {
      loadChangeOnboardingData();
    }
  }, [accounts]);

  // Load Reliability Framework data (no authentication needed)
  useEffect(() => {
    loadReliabilityFrameworkData();
  }, []);

  // Load Postmortem data (no authentication needed)
  useEffect(() => {
    loadPostmortemData();
  }, []);

  // Load P1/P2 Incidents data when time range changes
  useEffect(() => {
    loadIncidentsData();
  }, [selectedTimeRange, customDateApplied, customDateRange]);

  // Load P3/P4 Incidents data when time range changes
  useEffect(() => {
    loadP3P4IncidentsData();
  }, [selectedTimeRange, customDateApplied, customDateRange]);

  // Load portfolio options on mount
  useEffect(() => {
    loadPortfolios();
  }, []);

  // Load platforms when portfolio changes (cascading filter)
  useEffect(() => {
    loadPlatforms();
  }, [selectedPortfolio]);

  // Load onboarding data when filters change or when Certification/Change Onboarding/Reliability Framework/Incidents/Postmortem data is loaded
  useEffect(() => {
    loadOnboardingData();
  }, [selectedPortfolio, selectedPlatform, certificationData, changeOnboardingData, reliabilityFrameworkData, incidentsData, p3p4IncidentsData, postmortemDetailsData]);

  // Slice data for current page
  const paginatedData = onboardingData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Helper function to get cell styling based on value (matching Portfolio Health Tracker colors)
  const getCellStyle = (value: string) => {
    const lowerValue = value.toLowerCase().trim();
    
    if (lowerValue === 'yes' || lowerValue === 'onboarded') {
      return {
        bgcolor: '#4caf50 !important',  // Green - same as Portfolio Health Tracker
        color: '#fff !important',
        fontWeight: 600,
        '& .MuiChip-label': {
          color: '#fff !important'
        }
      };
    } else if (lowerValue === 'n/a') {
      return {
        bgcolor: '#9e9e9e !important',  // Grey - same as Portfolio Health Tracker
        color: '#fff !important',
        fontWeight: 600,
        '& .MuiChip-label': {
          color: '#fff !important'
        }
      };
    } else if (lowerValue === 'not available') {
      return {
        bgcolor: '#9e9e9e !important',  // Grey for Not Available
        color: '#fff !important',
        fontWeight: 600,
        '& .MuiChip-label': {
          color: '#fff !important'
        }
      };
    } else if (lowerValue === 'not onboarded' || lowerValue === 'no') {
      return {
        bgcolor: '#f44336 !important',  // Red - same as Portfolio Health Tracker
        color: '#fff !important',
        fontWeight: 600,
        '& .MuiChip-label': {
          color: '#fff !important'
        }
      };
    } else if (value.includes('%')) {
      // For percentage values (CMDB completeness), show green if it's a percentage
      return {
        bgcolor: '#4caf50 !important',  // Green - same as Portfolio Health Tracker
        color: '#fff !important',
        fontWeight: 600,
        '& .MuiChip-label': {
          color: '#fff !important'
        }
      };
    }
    
    // Default styling for other values
    return {};
  };

  // ─── Access guard ───────────────────────────────────────────────────────────
  const userEmail = accounts[0]?.username;
  if (!userEmail || !TOPS_ONBOARDING_ALLOWED_EMAILS.has(userEmail.toLowerCase())) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400, gap: 2 }}>
        <LockOutlined sx={{ fontSize: 64, color: '#bdbdbd' }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#424242' }}>
          Access Restricted
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 420 }}>
          You don't have permission to view Tops Onboarding.
          Please contact your administrator if you believe this is an error.
        </Typography>
        {userEmail && (
          <Typography variant="caption" color="text.disabled">
            Logged in as: {userEmail}
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700, color: '#0067b3' }}>
        TOP Onboarding Status
      </Typography>

      {/* Filters Section */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          {/* Portfolio Filter */}
          <FormControl size="small" sx={{ minWidth: 250 }}>
            <InputLabel>Select Portfolio</InputLabel>
            <Select
              value={selectedPortfolio}
              label="Select Portfolio"
              onChange={(e) => setSelectedPortfolio(e.target.value)}
              sx={{ bgcolor: 'white' }}
            >
              <MenuItem value="all">All Portfolios</MenuItem>
              {portfolioOptions.map(portfolio => (
                <MenuItem key={portfolio} value={portfolio}>
                  {portfolio}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Platform Filter */}
          <FormControl size="small" sx={{ minWidth: 250 }}>
            <InputLabel>Select Platform</InputLabel>
            <Select
              value={selectedPlatform}
              label="Select Platform"
              onChange={(e) => setSelectedPlatform(e.target.value)}
              sx={{ bgcolor: 'white' }}
            >
              <MenuItem value="all">All Platforms</MenuItem>
              {platformOptions.map(platform => (
                <MenuItem key={platform} value={platform}>
                  {platform}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Time Range Filter */}
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Time Range</InputLabel>
            <Select
              value={selectedTimeRange}
              label="Time Range"
              onChange={(e) => {
                const value = e.target.value;
                setSelectedTimeRange(value);
                if (value === 'custom') {
                  setShowCustomDatePicker(true);
                } else {
                  setShowCustomDatePicker(false);
                  setCustomDateApplied(false);
                }
              }}
              sx={{
                fontSize: '13px',
                height: '40px',
                backgroundColor: '#ffffff',
              }}
            >
              <MenuItem value="7d" sx={{ fontSize: '13px' }}>Last 7 Days</MenuItem>
              <MenuItem value="30d" sx={{ fontSize: '13px' }}>Last 30 Days</MenuItem>
              <MenuItem value="90d" sx={{ fontSize: '13px' }}>Last 90 Days</MenuItem>
              <MenuItem value="ytd" sx={{ fontSize: '13px' }}>Year to Date</MenuItem>
              <MenuItem value="custom" sx={{ fontSize: '13px' }}>Custom Range</MenuItem>
            </Select>
          </FormControl>

          {/* Custom Date Range Picker */}
          {showCustomDatePicker && (
            <>
              <TextField
                label="Start Date"
                type="date"
                size="small"
                value={customDateRange.start}
                onChange={(e) => {
                  const newStart = e.target.value;
                  setCustomDateRange({ 
                    start: newStart, 
                    end: customDateRange.end && new Date(customDateRange.end) >= new Date(newStart) 
                      ? customDateRange.end 
                      : '' 
                  });
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{ 
                  bgcolor: 'white', 
                  minWidth: 150,
                  '& .MuiInputBase-root': {
                    fontSize: '13px',
                    height: '40px'
                  }
                }}
              />
              <TextField
                label="End Date"
                type="date"
                size="small"
                value={customDateRange.end}
                onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min: customDateRange.start || undefined,
                  max: new Date().toISOString().split('T')[0]
                }}
                disabled={!customDateRange.start}
                sx={{ 
                  bgcolor: 'white', 
                  minWidth: 150,
                  '& .MuiInputBase-root': {
                    fontSize: '13px',
                    height: '40px'
                  }
                }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  if (customDateRange.start && customDateRange.end && customDateRange.end >= customDateRange.start) {
                    setCustomDateApplied(true);
                    setSelectedTimeRange('custom');
                  }
                }}
                disabled={!customDateRange.start || !customDateRange.end || customDateRange.end < customDateRange.start}
                sx={{ 
                  height: '40px',
                  minWidth: '80px',
                  bgcolor: '#0067b3',
                  '&:hover': {
                    bgcolor: '#004c8c'
                  }
                }}
              >
                Apply
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setCustomDateRange({ start: '', end: '' });
                  setCustomDateApplied(false);
                  setShowCustomDatePicker(false);
                  setSelectedTimeRange('30d');
                }}
                sx={{ 
                  height: '40px',
                  minWidth: '80px'
                }}
              >
                Clear
              </Button>
            </>
          )}
        </Box>
      </Paper>

      {/* Loading State */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Data Table */}
      <Paper elevation={2}>
        <TableContainer>
          <Table 
            size="small" 
            stickyHeader
            sx={{
              '& .MuiTableCell-root': {
                border: '1px solid #e0e0e0',
              }
            }}
          >
            <TableHead>
              {/* First row - Main headers */}
              <TableRow>
                <TableCell rowSpan={2} align="center" sx={{ fontWeight: 700, bgcolor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>App ID</TableCell>
                <TableCell rowSpan={2} align="center" sx={{ fontWeight: 700, bgcolor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>Portfolio Name</TableCell>
                <TableCell rowSpan={2} align="center" sx={{ fontWeight: 700, bgcolor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>Platform Name</TableCell>
                <TableCell rowSpan={2} align="center" sx={{ fontWeight: 700, bgcolor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>App Name</TableCell>
                <TableCell rowSpan={2} align="center" sx={{ fontWeight: 700, bgcolor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>Service Owner (optional)</TableCell>
                <TableCell rowSpan={2} align="center" sx={{ fontWeight: 700, bgcolor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>CMDB/MSA or App Id completeness</TableCell>
                <TableCell rowSpan={2} align="center" sx={{ fontWeight: 700, bgcolor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>L1.5 Support Requirements</TableCell>
                <TableCell rowSpan={2} align="center" sx={{ fontWeight: 700, bgcolor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>Status Page</TableCell>
                <TableCell rowSpan={2} align="center" sx={{ fontWeight: 700, bgcolor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>MVC</TableCell>
                <TableCell rowSpan={2} align="center" sx={{ fontWeight: 700, bgcolor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>MOP</TableCell>
                <TableCell rowSpan={2} align="center" sx={{ fontWeight: 700, bgcolor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>Hedwig</TableCell>
                <TableCell colSpan={2} align="center" sx={{ fontWeight: 700, bgcolor: '#e3f2fd', borderBottom: '1px solid #90caf9' }}>Critical Incident Response</TableCell>
                <TableCell rowSpan={2} align="center" sx={{ fontWeight: 700, bgcolor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>Change Management</TableCell>
                <TableCell rowSpan={2} align="center" sx={{ fontWeight: 700, bgcolor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>Postmortems (RCA)</TableCell>
              </TableRow>
              {/* Second row - Sub headers for Critical Incident Response */}
              <TableRow>
                <TableCell align="center" sx={{ fontWeight: 700, bgcolor: '#e3f2fd', borderBottom: '2px solid #ddd' }}>P1/P2 MI</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700, bgcolor: '#e3f2fd', borderBottom: '2px solid #ddd' }}>P3/P4 MI</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {onboardingData.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No data available. Please configure the backend API endpoint.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {paginatedData.map((row, index) => (
                <TableRow key={index} hover>
                  <TableCell align="center">
                    <Link
                      href={`https://mac.maersk.io/application/${row.appId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: '#0067b3',
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      {row.appId}
                    </Link>
                  </TableCell>
                  <TableCell align="center">{row.portfolioName}</TableCell>
                  <TableCell align="center">{row.platformName}</TableCell>
                  <TableCell align="center">{row.appName}</TableCell>
                  <TableCell align="center">
                    {(() => {
                      // Parse owner names and emails
                      const ownerNames = row.serviceOwner.split(/[,;]/).map(name => name.trim()).filter(name => name && name !== 'N/A');
                      const ownerEmails = row.serviceOwnerEmail.split(/[,;]/).map(email => email.trim()).filter(email => email && email !== 'N/A');
                      
                      if (ownerNames.length === 0 || row.serviceOwner === 'N/A') {
                        return <span>N/A</span>;
                      }
                      
                      return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                          {ownerNames.map((name, idx) => (
                            <Tooltip 
                              key={idx} 
                              title={ownerEmails[idx] || 'No email'} 
                              arrow 
                              placement="top"
                            >
                              <Chip
                                label={name}
                                size="small"
                                sx={{
                                  cursor: 'pointer',
                                  fontSize: '0.75rem',
                                  height: '24px',
                                  bgcolor: '#e3f2fd',
                                  '&:hover': {
                                    bgcolor: '#bbdefb',
                                  }
                                }}
                              />
                            </Tooltip>
                          ))}
                        </Box>
                      );
                    })()}
                  </TableCell>
                  <TableCell align="center">
                    {row.cmdbCompleteness !== 'N/A' ? (
                      <Link
                        href={`https://msa.maersk.io/asset_lookup?applicationId=${row.appId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ textDecoration: 'none' }}
                        title={`View details for ${row.appId} in MSA`}
                      >
                        <Chip 
                          label={row.cmdbCompleteness}
                          size="small"
                          component="span"
                          sx={{
                            ...getCellStyle(row.cmdbCompleteness),
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              opacity: 0.8,
                              transform: 'scale(1.05)',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                            }
                          }}
                        />
                      </Link>
                    ) : (
                      <Chip 
                        label={row.cmdbCompleteness}
                        size="small"
                        sx={getCellStyle(row.cmdbCompleteness)}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {row.l15ApplicationSupport !== 'N/A' ? (
                      <Link
                        href={`https://msa.maersk.io/update_instance?applicationName=${encodeURIComponent(row.appName)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Chip 
                          label={row.l15ApplicationSupport}
                          size="small"
                          sx={{
                            ...getCellStyle(row.l15ApplicationSupport),
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.8
                            }
                          }}
                        />
                      </Link>
                    ) : (
                      <Chip 
                        label={row.l15ApplicationSupport}
                        size="small"
                        sx={getCellStyle(row.l15ApplicationSupport)}
                      />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={row.statusPage}
                      size="small"
                      sx={getCellStyle(row.statusPage)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={row.mvc}
                      size="small"
                      sx={getCellStyle(row.mvc)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={row.mop}
                      size="small"
                      sx={getCellStyle(row.mop)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={row.hedwig}
                      size="small"
                      sx={getCellStyle(row.hedwig)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={row.p1p2Incidents.toString()}
                      size="small"
                      sx={getCellStyle(row.p1p2Incidents > 0 ? 'Yes' : 'N/A')}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={row.p3p4Incidents.toString()}
                      size="small"
                      sx={getCellStyle(row.p3p4Incidents > 0 ? 'Yes' : 'N/A')}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Link
                      href="https://flux.maersk.io/application-onboarding"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ textDecoration: 'none' }}
                    >
                      <Chip 
                        label={row.changeManagement}
                        size="small"
                        sx={{
                          ...getCellStyle(row.changeManagement),
                          cursor: 'pointer',
                          '&:hover': {
                            opacity: 0.8
                          }
                        }}
                      />
                    </Link>
                  </TableCell>
                  <TableCell align="center">
                    {row.postmortems === 'Yes' ? (
                      <Link
                        href={`https://sre-postmortem-prod.maersk-digital.net/?app=${encodeURIComponent(row.appName)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ textDecoration: 'none' }}
                      >
                        <Chip 
                          label={row.postmortems}
                          size="small"
                          sx={{
                            ...getCellStyle(row.postmortems),
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.8
                            }
                          }}
                        />
                      </Link>
                    ) : (
                      <Chip 
                        label={row.postmortems}
                        size="small"
                        sx={getCellStyle(row.postmortems)}
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={onboardingData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
    </Box>
  );
};

export default TopsOnboarding;